import React from "react";

import { FlexGrid, Input, InputFile } from "@amphore-dev/amphore-lib";
import { Form, Formik, FormikProps } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import { emailSchema } from "@/utils/UForms";

import { TCompanyFormValues } from "@/types";

export interface ICompanyFormProps {
	initialValues: TCompanyFormValues;
	onSubmit: (values: TCompanyFormValues) => void;
	/**
	 * Formik's `innerRef` — the form has no submit button of its own, the
	 * parent hosts it (step card, modal footer...) and calls
	 * `ref.current?.submitForm()`.
	 */
	innerRef?: React.Ref<FormikProps<TCompanyFormValues>>;
	/** Unique per page: it's the logo input's `id`, wired to its label. */
	logoInputId?: string;
}

const validationSchema = () =>
	Yup.object().shape({
		name: Yup.string().trim().required(t("forms.required")),
		email: emailSchema(),
	});

type TTextField = Exclude<keyof TCompanyFormValues, "logo">;

/**
 * Creation form of a company (name, legal ids, contact, address, logo) —
 * shared by the onboarding's company step and the companies page's "new
 * company" modal. Editing an existing one goes through `EditableCard`.
 *
 * Hand-composed on FlexGrid rather than FormRenderer: the lib bundles its
 * own Formik, so FormRenderer's wrapper reads a different FormikContext
 * than the one this form's <Formik> provides and crashes on mount.
 */
export const CompanyForm: React.FC<ICompanyFormProps> = ({
	initialValues,
	onSubmit,
	innerRef,
	logoInputId = "company-form-logo",
}) => {
	return (
		<Formik
			innerRef={innerRef}
			initialValues={initialValues}
			validationSchema={validationSchema()}
			onSubmit={(values) => onSubmit(values)}
		>
			{({ values, errors, touched, setFieldValue }) => {
				const text = (
					name: TTextField,
					label: string,
					extra: Partial<React.ComponentProps<typeof Input>> = {}
				) => (
					<Input
						name={name}
						label={label}
						value={values[name]}
						onChange={(value) => setFieldValue(name, value)}
						error={touched[name] ? errors[name] : undefined}
						{...extra}
					/>
				);
				return (
					<Form className="flex flex-col gap-4">
						<FlexGrid columns={2} minItemWidth="240px" gap="1rem">
							{text("name", t("company.name"), {
								placeholder: t(
									"onboarding.steps.company.namePlaceholder"
								),
								required: true,
								autoComplete: "organization",
							})}
							{text("siren", t("company.siren"), {
								placeholder: "123 456 789",
							})}
							{text("vatNumber", t("company.vatNumber"), {
								placeholder: "FR12 123456789",
							})}
							{text("email", t("company.email"), {
								placeholder: "contact@exemple.fr",
								autoComplete: "email",
								inputMode: "email",
							})}
						</FlexGrid>
						{text("address", t("company.address"), {
							autoComplete: "street-address",
						})}
						<FlexGrid columns={2} minItemWidth="240px" gap="1rem">
							{text("postalCode", t("company.postalCode"), {
								autoComplete: "postal-code",
							})}
							{text("city", t("company.city"), {
								autoComplete: "address-level2",
							})}
						</FlexGrid>
						<div className="flex flex-col gap-1.5">
							<label
								htmlFor={logoInputId}
								className="text-xs font-semibold"
							>
								{t("company.logo")}
							</label>
							<InputFile
								id={logoInputId}
								name="logo"
								value={values.logo}
								onChange={(file) => setFieldValue("logo", file)}
								accept="image/*"
								orientation="horizontal"
								placeholder={t(
									"onboarding.steps.company.logoPlaceholder"
								)}
							/>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};
